import type { EditorView } from "@codemirror/view";

// LivePreviewCursor is the cursor context handed to every live-preview decoration
// builder. It carries the raw selection positions AND the precomputed 1-based
// line numbers, so builders no longer each re-derive `doc.lineAt(cursor.head)`.
//
// The load-bearing invariant: when the editor is NOT focused, EVERY field is -1.
// The live-preview "reveal raw markdown on the cursor's line" affordance only
// makes sense while editing; with no focus there is no meaningful cursor, so no
// line and no range can match a node, and ALL markdown renders styled (preview).
//
// Why -1 and not a real position (this was a real bug): the previous code used
// `doc.length` as the unfocused "cursor", reasoning it sat "past the end" so no
// line would match. But `doc.length` is the last VALID position — it resolves to
// the LAST line via `doc.lineAt()`. So a blurred editor always revealed the last
// paragraph's raw syntax, and adding a trailing newline shifted which paragraph
// looked "active". A line number can be out of range (-1); a position cannot.
export interface LivePreviewCursor {
  head: number;
  anchor: number;
  from: number;
  to: number;
  cursorLine: number; // 1-based line of head, or -1 when unfocused
  anchorLine: number; // 1-based line of anchor, or -1 when unfocused
}

// activeCursor derives the LivePreviewCursor from the view. Unfocused → all -1.
export function activeCursor(view: EditorView): LivePreviewCursor {
  if (!view.hasFocus) {
    return { head: -1, anchor: -1, from: -1, to: -1, cursorLine: -1, anchorLine: -1 };
  }
  const main = view.state.selection.main;
  const doc = view.state.doc;
  return {
    head: main.head,
    anchor: main.anchor,
    from: main.from,
    to: main.to,
    cursorLine: doc.lineAt(main.head).number,
    anchorLine: doc.lineAt(main.anchor).number,
  };
}
